import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as Anchor, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faSearch, faSliders } from '@fortawesome/free-solid-svg-icons';
import { useCatalogData } from '../CatalogData/CatalogDataProvider';
import moneda from '../moneda';
import {
    cleanCatalogText,
    filterCatalogProducts,
    normalizeCatalogId,
} from '../../utils/catalogSearch';
import './ProductsPage.css';
import 'react-toastify/dist/ReactToastify.css';

const ITEM_FIELDS = Array.from({ length: 10 }, (_, index) => `item${index + 1}`);
const PRIVATE_CATALOG_PARAMS = Object.freeze([
    'idPedido',
    'tokenConsulta',
    'consultaUrl',
]);

const normalizarItem = value => cleanCatalogText(value).toLocaleLowerCase('es-CO');

const removePrivateCatalogParams = (searchParams) => {
    PRIVATE_CATALOG_PARAMS.forEach(paramName => searchParams.delete(paramName));
    return searchParams;
};

const esPrecioValido = (precio) => Number.isFinite(Number(precio)) && Number(precio) >= 0;

const calcularPrecioMaximo = (productos) => {
    const precios = productos
        .map(producto => Number(producto?.precio))
        .filter(precio => Number.isFinite(precio) && precio >= 0);

    return precios.length > 0 ? Math.max(...precios) : 0;
};

const formatearPrecio = (precio) => {
    const valor = Number(precio);

    return Number.isFinite(valor)
        ? `${moneda} ${valor.toLocaleString('es-CO')}`
        : 'Precio por confirmar';
};

export default function ProductsPage() {
    const {
        productos,
        categorias,
        status,
        errors,
        ensureProductos,
        ensureCategorias,
        retryProductos,
        retryCategorias,
    } = useCatalogData();
    const [searchParams, setSearchParams] = useSearchParams();
    const [precioMinimo] = useState(0);
    const [precioSeleccionado, setPrecioSeleccionado] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const precioMaximo = useMemo(() => calcularPrecioMaximo(productos), [productos]);
    const precioActual = precioSeleccionado === null
        ? precioMaximo
        : Math.min(precioSeleccionado, precioMaximo);
    const searchText = cleanCatalogText(searchParams.get('buscar'));
    const searchCategoriesPending = status.productos === 'loaded'
        && Boolean(searchText)
        && (status.categorias === 'idle' || status.categorias === 'loading');
    const loading = status.productos === 'idle'
        || status.productos === 'loading'
        || searchCategoriesPending;
    const catalogError = status.productos === 'error' || errors.productos
        ? 'No pudimos cargar el catálogo. Revisa tu conexión e inténtalo nuevamente.'
        : '';

    useEffect(() => {
        ensureProductos().catch(() => {});
        ensureCategorias().catch(() => {});
    }, [ensureProductos, ensureCategorias]);

    useEffect(() => {
        setPrecioSeleccionado(null);
    }, [productos]);

    const handleRetryProducts = useCallback(() => {
        retryProductos().catch(() => {});

        if (status.categorias === 'error') {
            retryCategorias().catch(() => {});
        }
    }, [retryCategorias, retryProductos, status.categorias]);

    const opcionesItems = useMemo(() => {
        const itemsUnicos = new Map();

        productos.forEach((producto) => {
            ITEM_FIELDS.forEach((field) => {
                const textoLimpio = cleanCatalogText(producto?.[field]);
                const valorNormalizado = normalizarItem(textoLimpio);

                if (textoLimpio && !itemsUnicos.has(valorNormalizado)) {
                    itemsUnicos.set(valorNormalizado, textoLimpio);
                }
            });
        });

        return Array.from(itemsUnicos, ([value, label]) => ({ value, label }))
            .sort((firstItem, secondItem) => firstItem.label.localeCompare(secondItem.label, 'es-CO'));
    }, [productos]);

    const categoriasPublicas = useMemo(() => {
        const categoriasConProductos = new Set(
            productos
                .map(producto => normalizeCatalogId(producto?.idCategoria))
                .filter(Boolean)
        );

        return categorias
            .map(categoria => ({
                id: normalizeCatalogId(categoria?.idCategoria),
                nombre: cleanCatalogText(categoria?.categoria),
            }))
            .filter(categoria => (
                categoria.id
                && categoria.nombre
                && categoriasConProductos.has(categoria.id)
            ));
    }, [categorias, productos]);

    const categoriaSolicitada = normalizeCatalogId(searchParams.get('categoria'));
    const categoriaSeleccionada = categoriasPublicas.some(
        categoria => categoria.id === categoriaSolicitada
    )
        ? categoriaSolicitada
        : '';

    const categoriasPorId = useMemo(() => {
        return categoriasPublicas.reduce((categoriasMap, categoria) => {
            categoriasMap[categoria.id] = categoria.nombre;
            return categoriasMap;
        }, {});
    }, [categoriasPublicas]);

    const tienePreciosValidos = useMemo(
        () => productos.some(producto => esPrecioValido(producto?.precio)),
        [productos]
    );

    const obtenerImagen = (item) => {
        return item.imagen1 || item.imagen2 || item.imagen3 || item.imagen4 || null;
    };

    const handleItemSelect = (item) => {
        setSelectedItems(item === '' ? [] : [item]);
    };

    const updateCategoriaParam = (categoriaId) => {
        const normalizedCategoryId = normalizeCatalogId(categoriaId);

        setSearchParams((currentParams) => {
            const nextParams = removePrivateCatalogParams(
                new URLSearchParams(currentParams)
            );

            if (normalizedCategoryId) {
                nextParams.set('categoria', normalizedCategoryId);
            } else {
                nextParams.delete('categoria');
            }

            return nextParams;
        });
    };

    const updateSearchParam = (nextSearchText) => {
        const cleanedSearchText = cleanCatalogText(nextSearchText);

        if (cleanedSearchText === searchText) {
            return;
        }

        setSearchParams((currentParams) => {
            const nextParams = removePrivateCatalogParams(
                new URLSearchParams(currentParams)
            );

            if (cleanedSearchText) {
                nextParams.set('buscar', cleanedSearchText);
            } else {
                nextParams.delete('buscar');
            }

            return nextParams;
        }, { replace: true });
    };

    const limpiarFiltros = () => {
        setSearchParams((currentParams) => {
            const nextParams = removePrivateCatalogParams(
                new URLSearchParams(currentParams)
            );
            nextParams.delete('categoria');
            nextParams.delete('buscar');
            return nextParams;
        });
        setSelectedItems([]);
        setPrecioSeleccionado(null);
        setFiltersOpen(false);
    };

    const productosOrdenadosPorBusqueda = useMemo(
        () => filterCatalogProducts(productos, categorias, searchText),
        [categorias, productos, searchText]
    );

    const filterProductos = (producto) => {
        const categoriaMatch = categoriaSeleccionada === ''
            || normalizeCatalogId(producto?.idCategoria) === categoriaSeleccionada;
        const precioProducto = Number(producto?.precio);
        const precioMatch = !Number.isFinite(precioProducto) || !tienePreciosValidos || (precioProducto >= precioMinimo && precioProducto <= precioActual);
        const itemMatches = selectedItems.length === 0 || selectedItems.some(item => (
            ITEM_FIELDS.some(field => normalizarItem(producto?.[field]) === item)
        ));

        return categoriaMatch && precioMatch && itemMatches;
    };

    const productosFiltrados = productosOrdenadosPorBusqueda.filter(filterProductos);
    const hayFiltrosActivos = Boolean(
        searchText.trim() ||
        categoriaSeleccionada ||
        selectedItems.length ||
        precioActual < precioMaximo
    );
    const filtrosDeshabilitados = loading || productos.length === 0;

    return (
        <section className='catalogCompactContent' aria-label='Catálogo de productos'>
            <div className='catalogControls'>
                <div className='catalogControls__search'>
                    <label className='catalogSrOnly' htmlFor='catalog-search'>Buscar productos</label>
                    <FontAwesomeIcon icon={faSearch} aria-hidden='true' />
                    <input
                        id='catalog-search'
                        type='search'
                        placeholder='Buscar producto o categoría'
                        value={searchText}
                        onChange={(event) => updateSearchParam(event.target.value)}
                        autoComplete='off'
                        disabled={filtrosDeshabilitados}
                    />
                </div>

                <button
                    type='button'
                    className={`catalogControls__toggle${filtersOpen ? ' is-open' : ''}`}
                    aria-expanded={filtersOpen}
                    aria-controls='catalog-secondary-filters'
                    onClick={() => setFiltersOpen(currentValue => !currentValue)}
                    disabled={filtrosDeshabilitados}
                >
                    <FontAwesomeIcon icon={faSliders} aria-hidden='true' />
                    Filtros
                </button>

                <div
                    id='catalog-secondary-filters'
                    className={`catalogControls__secondary${filtersOpen ? ' is-open' : ''}`}
                >
                    <div className='catalogField'>
                        <label htmlFor='catalog-category'>Categoría</label>
                        <select
                            id='catalog-category'
                            value={categoriaSeleccionada}
                            onChange={(event) => updateCategoriaParam(event.target.value)}
                            disabled={filtrosDeshabilitados}
                        >
                            <option value=''>Todas</option>
                            {categoriasPublicas.map(categoria => (
                                <option key={categoria.id} value={categoria.id}>
                                    {categoria.nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className='catalogField'>
                        <label htmlFor='catalog-item'>Compatibilidad</label>
                        <select
                            id='catalog-item'
                            value={selectedItems.length > 0 ? selectedItems[0] : ''}
                            onChange={(event) => handleItemSelect(event.target.value)}
                            disabled={filtrosDeshabilitados}
                        >
                            <option value=''>Todos los ítems</option>
                            {opcionesItems.map(item => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className='catalogField catalogField--price'>
                        <div className='catalogField__priceLabel'>
                            <label htmlFor='catalog-price'>Hasta</label>
                            <output htmlFor='catalog-price'>
                                {tienePreciosValidos ? formatearPrecio(precioActual) : 'Sin precios'}
                            </output>
                        </div>
                        <input
                            id='catalog-price'
                            type='range'
                            min={precioMinimo}
                            max={precioMaximo}
                            value={precioActual}
                            onChange={(event) => setPrecioSeleccionado(Number(event.target.value))}
                            disabled={!tienePreciosValidos || precioMaximo <= precioMinimo}
                        />
                    </div>
                </div>

                <div className='catalogControls__actions' aria-live='polite'>
                    <span>
                        {loading
                            ? 'Cargando...'
                            : `${productosFiltrados.length} ${productosFiltrados.length === 1 ? 'producto' : 'productos'}`}
                    </span>
                    {hayFiltrosActivos && (
                        <button type='button' onClick={limpiarFiltros}>
                            <FontAwesomeIcon icon={faRotateLeft} aria-hidden='true' />
                            Limpiar
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className='catalogProductGrid catalogProductGrid--loading' aria-busy='true' aria-label='Cargando productos'>
                    {Array.from({ length: 8 }, (_, index) => (
                        <div className='catalogProductSkeleton' key={index} />
                    ))}
                </div>
            ) : catalogError ? (
                <div className='catalogFeedback' role='alert'>
                    <h2>No fue posible cargar los productos.</h2>
                    <p>{catalogError}</p>
                    <button type='button' onClick={handleRetryProducts}>Reintentar</button>
                </div>
            ) : productos.length === 0 ? (
                <div className='catalogFeedback' role='status'>
                    <h2>Pronto encontrarás productos aquí.</h2>
                    <p>Estamos actualizando el catálogo para ti.</p>
                </div>
            ) : productosFiltrados.length === 0 ? (
                <div className='catalogFeedback' role='status'>
                    <h2>No encontramos productos con esa búsqueda.</h2>
                    <p>Puedes intentar con otro término o ajustar los filtros.</p>
                    <p className='catalogFeedback__help'>
                        Escríbenos por WhatsApp y te ayudamos a encontrar el tóner, tinta o repuesto que necesitas.
                    </p>
                    <button type='button' onClick={limpiarFiltros}>Restablecer filtros</button>
                </div>
            ) : (
                <div className='catalogProductGrid'>
                    {productosFiltrados.map(item => {
                        const titulo = cleanCatalogText(item.titulo) || 'Producto';
                        const categoria = categoriasPorId[normalizeCatalogId(item.idCategoria)] || 'Insumos';
                        const imagen = obtenerImagen(item);
                        const precioAnteriorValido = esPrecioValido(item.precioAnterior) && Number(item.precioAnterior) > 0;

                        return (
                            <Anchor
                                className='catalogProductCard'
                                key={item.idProducto}
                                to={`/producto/${item.idProducto}/${titulo.replace(/\s+/g, '-')}`}
                                aria-label={`Ver ${titulo}`}
                            >
                                <div className='catalogProductCard__media'>
                                    {imagen ? (
                                        <img
                                            src={imagen}
                                            alt={`${titulo} - ${categoria}`}
                                            loading='lazy'
                                            decoding='async'
                                        />
                                    ) : (
                                        <span>Imagen no disponible</span>
                                    )}
                                </div>
                                <div className='catalogProductCard__body'>
                                    <span className='catalogProductCard__category'>{categoria}</span>
                                    <h2>{titulo}</h2>
                                    <div className='catalogProductCard__prices'>
                                        <strong>{formatearPrecio(item.precio)}</strong>
                                        {precioAnteriorValido && (
                                            <span>{formatearPrecio(item.precioAnterior)}</span>
                                        )}
                                    </div>
                                </div>
                            </Anchor>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
